import type { RuleModule } from '../engine/registry';
import { OK, reject, type GameRuntime, type GameState, type PlacementOpts } from '../engine/state';
import { notePiecePlaced, placementLegal } from '../engine/placement';
import { bagContains, type ResourceBag } from '../engine/gameDef';
import type { SeatIdx } from '../types';
import type { VertexId } from '../board/axial';

// Building (outline §7; plan §8.2): roads/settlements/cities in the action window. Costs
// return to the Supply; POOLS ARE DERIVED from occupancy (plan §4.1 single source of
// truth) and bind as hard limits (outline §14.19). Roads never continue through the far
// side of an opponent's (or neutral) building (outline §14.12).

/** Is `seat` the owner of an open build window? Paired/SBP states are recognized here so
 *  their modules need no validator replacement (plan §7.6; the states land in H1-S11). */
export function inBuildWindow(state: GameState, seat: SeatIdx): boolean {
  const p = state.phase;
  return (p.t === 'ACTION' || p.t === 'PARTNER_ACTION' || p.t === 'SBP') && p.seat === seat;
}

/** Placed-piece count for a seat (pools derive from this — never stored). */
export function placedCount(state: GameState, seat: SeatIdx, piece: string): number {
  let n = 0;
  for (const domain of [state.board.vertices, state.board.edges, state.board.hexes]) {
    for (const p of Object.values(domain)) if (p.owner === seat && p.piece === piece) n += 1;
  }
  return n;
}

export function poolRemaining(state: GameState, env: GameRuntime, seat: SeatIdx, piece: string): number {
  const def = env.def.pieces.find((p) => p.id === piece);
  const pool = env.map.overrides.piecePools?.[piece] ?? def?.pool ?? 0;
  return pool - placedCount(state, seat, piece);
}

/** Charge a cost bag from the seat's hand back into the Supply. Caller validated coverage. */
export function payCost(state: GameState, seat: SeatIdx, cost: ResourceBag): void {
  for (const [rid, n] of Object.entries(cost)) {
    state.seats[seat]!.hand[rid] = (state.seats[seat]!.hand[rid] ?? 0) - n;
    if (state.seats[seat]!.hand[rid] === 0) delete state.seats[seat]!.hand[rid]; // canonical bags (plan §3.1)
    state.supply[rid] = (state.supply[rid] ?? 0) + n;
  }
}

/**
 * Road-placement legality MINUS cost/pool (Roadworks reuses this — outline §14.17). The
 * classic `road` placement rule (H20-S0d registers it; callers ask the registry, never this
 * name). `excludeEdge` evaluates as if that edge were empty.
 */
export function roadPlacementLegal(state: GameState, env: GameRuntime, seat: SeatIdx, edge: string, opts?: PlacementOpts): boolean {
  const e = env.board.edges.get(edge);
  if (!e) return false;
  if (state.board.edges[edge] && edge !== opts?.excludeEdge) return false;
  for (const v of e.vertices) {
    const building = state.board.vertices[v];
    if (building && building.owner === seat) return true; // own building anchors always
    if (building) continue; // an opponent/neutral building CAPS continuation through v (outline §14.12)
    const hasOwnRoad = env.board.vertices
      .get(v)!
      .edges.some((other) => other !== edge && other !== opts?.excludeEdge && state.board.edges[other]?.owner === seat);
    if (hasOwnRoad) return true;
  }
  return false;
}

/** Settlement-site legality MINUS cost/pool: empty, distance-clear, touched by an own road.
 *  The classic `settlement` placement rule (H20-S0d). */
export function settlementSiteLegal(state: GameState, env: GameRuntime, seat: SeatIdx, vertex: VertexId): boolean {
  const v = env.board.vertices.get(vertex);
  if (!v) return false;
  if (state.board.vertices[vertex]) return false;
  for (const nb of v.neighbors) if (state.board.vertices[nb]) return false; // distance rule — ANY building (outline §14.11)
  return v.edges.some((e) => state.board.edges[e]?.owner === seat);
}

export const buildingModule: RuleModule = {
  id: 'building',
  register(ctx) {
    // H20-S0d: the classic rules, REGISTERED — BUILD_*, Road Building and the bot all ask the
    // registry, so a module that replaces one replaces it for every one of them
    ctx.placementRule('road', roadPlacementLegal);
    ctx.placementRule('settlement', (state, env, seat, site) => settlementSiteLegal(state, env, seat, site));

    // 'pieces' VP source (outline §12): every placed piece scores its def vp
    ctx.vpSource('pieces', (state, env, seat) => {
      let sum = 0;
      for (const piece of env.def.pieces) {
        if (piece.vp > 0) sum += placedCount(state, seat, piece.id) * piece.vp;
      }
      return sum;
    });

    ctx.action('BUILD_ROAD', {
      enumerate(state, env, seat) {
        if (!inBuildWindow(state, seat)) return null;
        return [...env.board.edges.keys()].map((edge) => ({ edge }));
      },
      validate(state, action, env) {
        if (!inBuildWindow(state, action.seat)) return reject('WRONG_PHASE');
        const edge = action.payload['edge'];
        if (typeof edge !== 'string') return reject('BAD_PAYLOAD');
        if (!placementLegal(state, env, action.seat, 'road', edge)) return reject('ILLEGAL_PLACEMENT');
        if (poolRemaining(state, env, action.seat, 'road') < 1) return reject('POOL_EMPTY');
        if (!bagContains(state.seats[action.seat]!.hand, env.def.costs['road'] ?? {})) return reject('CANNOT_AFFORD');
        return OK;
      },
      apply(state, action, env, emit) {
        const edge = String(action.payload['edge']);
        payCost(state, action.seat, env.def.costs['road'] ?? {});
        state.board.edges[edge] = { piece: 'road', owner: action.seat };
        emit({ kind: 'BUILT', vis: 'public', seat: action.seat, piece: 'road', at: edge, free: false });
        notePiecePlaced(state, env, action.seat, 'road', edge, emit);
      },
    });

    ctx.action('BUILD_SETTLEMENT', {
      enumerate(state, env, seat) {
        if (!inBuildWindow(state, seat)) return null;
        return [...env.board.vertices.keys()].map((vertex) => ({ vertex }));
      },
      validate(state, action, env) {
        if (!inBuildWindow(state, action.seat)) return reject('WRONG_PHASE');
        const vertex = action.payload['vertex'];
        if (typeof vertex !== 'string') return reject('BAD_PAYLOAD');
        if (!placementLegal(state, env, action.seat, 'settlement', vertex)) return reject('ILLEGAL_PLACEMENT');
        if (poolRemaining(state, env, action.seat, 'settlement') < 1) return reject('POOL_EMPTY'); // outline §14.19
        if (!bagContains(state.seats[action.seat]!.hand, env.def.costs['settlement'] ?? {})) return reject('CANNOT_AFFORD');
        return OK;
      },
      apply(state, action, env, emit) {
        const vertex = String(action.payload['vertex']);
        payCost(state, action.seat, env.def.costs['settlement'] ?? {});
        state.board.vertices[vertex] = { piece: 'settlement', owner: action.seat };
        emit({ kind: 'BUILT', vis: 'public', seat: action.seat, piece: 'settlement', at: vertex, free: false });
        notePiecePlaced(state, env, action.seat, 'settlement', vertex, emit);
      },
    });

    ctx.action('BUILD_CITY', {
      enumerate(state, _env, seat) {
        if (!inBuildWindow(state, seat)) return null;
        return Object.entries(state.board.vertices)
          .filter(([, p]) => p.owner === seat && p.piece === 'settlement')
          .map(([vertex]) => ({ vertex }));
      },
      validate(state, action, env) {
        if (!inBuildWindow(state, action.seat)) return reject('WRONG_PHASE');
        const vertex = action.payload['vertex'];
        if (typeof vertex !== 'string') return reject('BAD_PAYLOAD');
        const cur = state.board.vertices[vertex];
        if (!cur || cur.owner !== action.seat || cur.piece !== 'settlement') return reject('NOT_YOUR_SETTLEMENT'); // outline §7
        if (poolRemaining(state, env, action.seat, 'city') < 1) return reject('POOL_EMPTY');
        if (!bagContains(state.seats[action.seat]!.hand, env.def.costs['city'] ?? {})) return reject('CANNOT_AFFORD');
        return OK;
      },
      apply(state, action, env, emit) {
        const vertex = String(action.payload['vertex']);
        payCost(state, action.seat, env.def.costs['city'] ?? {});
        state.board.vertices[vertex] = { piece: 'city', owner: action.seat }; // in place; the settlement piece frees itself (derived pools)
        emit({ kind: 'BUILT', vis: 'public', seat: action.seat, piece: 'city', at: vertex, free: false });
        notePiecePlaced(state, env, action.seat, 'city', vertex, emit);
      },
    });
  },
};
